import React, { useState, useEffect } from 'react';
import { Modal, Button, Row, Col, Alert, Spinner } from 'react-bootstrap';
import './PipelineModal.css';

// Import extracted components
import { PIPELINE_STEPS, DEFAULT_KPIS, DEFAULT_EVENT_VARS } from './pipeline/constants';
import { formatApiError } from './pipeline/utils';
import { 
  executeConfigUnpackStep,
  executeScalingReferenceStep,
  executeWeightsMetricStep,
  executePhasesScorerAnomaliesStep,
  executeEventLogFPSuppressionStep,
  executeLiftLagRCAStep,
  executeSensorHealthTrendsStep,
  executeTieredMLStep,
  executeAssembleResultStep,
  executeAuditBundleStep
} from './pipeline/stepExecutors';
import { StepNavigation, ProgressBar } from './pipeline/StepNavigation';
import { 
  ConfigUnpackResults, 
  ScalingReferenceResults, 
  WeightsMetricResults 
} from './pipeline/StepResults';
import { 
  PhasesScorerAnomaliesResults, 
  EventLogFPSuppressionResults, 
  LiftLagRCAResults,
  SensorHealthTrendsResults,
  TieredMLResults,
  AssembleResultResults,
  AuditBundleResults
} from './pipeline/AdvancedStepResults';

const PipelineModal = ({ 
  show, 
  onHide, 
  projectId,
  projectData,
  excelData,
  googleSheetsData,
  googleSheetsInfo 
}) => {
  // State management
  const [currentStep, setCurrentStep] = useState(0);
  const [stepResults, setStepResults] = useState({});
  const [stepErrors, setStepErrors] = useState({});
  const [isProcessing, setIsProcessing] = useState(false);
  const [executionOrder, setExecutionOrder] = useState([]);
  const [error, setError] = useState('');

  // Reset state when modal opens
  useEffect(() => {
    if (show) {
      setCurrentStep(0);
      setStepResults({});
      setStepErrors({});
      setIsProcessing(false);
      setExecutionOrder([]);
      setError('');
    }
  }, [show]);

  // Step execution handlers
  const handleStepClick = (stepIndex) => {
    if (!isProcessing) {
      setCurrentStep(stepIndex);
    }
  };

  const executeStep = async (stepIndex) => {
    // Convert stepResults object to array for step executors
    const stepResultsArray = [];
    for (let i = 0; i < stepIndex; i++) {
      if (stepResults[i]) {
        stepResultsArray.push(stepResults[i]);
      }
    }
    
    const stepExecutors = [
      () => executeConfigUnpackStep(projectId, {
        kpis: DEFAULT_KPIS,
        event_vars: DEFAULT_EVENT_VARS,
        reference_run_ids: [1, 3], // Default reference runs
        growth_thr: 0.1,
        viability_thr: 90.0,
        max_lag_days: 3
      }),
      () => executeScalingReferenceStep(projectId, stepResultsArray),
      () => executeWeightsMetricStep(projectId, stepResultsArray),
      () => executePhasesScorerAnomaliesStep(projectId, stepResultsArray),
      () => executeEventLogFPSuppressionStep(projectId, stepResultsArray),
      () => executeLiftLagRCAStep(projectId, stepResultsArray),
      () => executeSensorHealthTrendsStep(projectId, stepResultsArray),
      () => executeTieredMLStep(projectId, stepResultsArray),
      () => executeAssembleResultStep(projectId, stepResultsArray),
      () => executeAuditBundleStep(projectId, stepResultsArray)
    ];

    if (stepExecutors[stepIndex]) {
      return await stepExecutors[stepIndex]();
    } else {
      // Placeholder for unimplemented steps
      await new Promise(resolve => setTimeout(resolve, 1500));
      return {
        stepName: PIPELINE_STEPS[stepIndex].name,
        data: `Mock result for ${PIPELINE_STEPS[stepIndex].name}`,
        message: `${PIPELINE_STEPS[stepIndex].name} completed successfully (placeholder implementation)`,
        isValid: true
      };
    }
  };

  const handleRunStep = async () => {
    setIsProcessing(true);
    setError('');
    
    try {
      console.log(`Running step ${currentStep + 1}: ${PIPELINE_STEPS[currentStep].name}`);
      
      const stepResult = await executeStep(currentStep);
      
      // Store result
      setStepResults(prev => ({
        ...prev,
        [currentStep]: {
          ...stepResult,
          timestamp: new Date()
        }
      }));

      // Clear any previous error for this step
      setStepErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[currentStep];
        return newErrors;
      });

      // Track execution order
      setExecutionOrder(prev => {
        if (!prev.includes(currentStep)) {
          return [...prev, currentStep];
        }
        return prev;
      });
      
      // Auto-advance to next step if not the last step
      if (currentStep < PIPELINE_STEPS.length - 1) {
        setCurrentStep(currentStep + 1);
      }
      
    } catch (error) {
      console.error(`Error in step ${currentStep + 1}:`, error);
      const formattedError = formatApiError(error);
      setError(`Failed to execute ${PIPELINE_STEPS[currentStep].name}: ${formattedError}`);
      setStepErrors(prev => ({
        ...prev,
        [currentStep]: { error: formattedError, timestamp: new Date() }
      }));
    } finally {
      setIsProcessing(false);
    }
  };

  const handleRunAllSteps = async () => {
    setIsProcessing(true);
    setError('');
    
    try {
      for (let i = 0; i < PIPELINE_STEPS.length; i++) {
        setCurrentStep(i);
        
        try {
          const stepResult = await executeStep(i);
          
          setStepResults(prev => ({
            ...prev,
            [i]: {
              ...stepResult,
              timestamp: new Date()
            }
          }));

          setExecutionOrder(prev => {
            if (!prev.includes(i)) {
              return [...prev, i];
            }
            return prev;
          });
          
        } catch (stepError) {
          console.error(`Error in step ${i + 1}:`, stepError);
          const formattedError = formatApiError(stepError);
          setStepErrors(prev => ({
            ...prev,
            [i]: { error: formattedError, timestamp: new Date() }
          }));
          // Continue with next step instead of breaking
        }
        
        // Small delay between steps for better UX
        await new Promise(resolve => setTimeout(resolve, 500));
      }
    } catch (error) {
      console.error('Error running all steps:', error);
      setError(`Pipeline execution failed: ${formatApiError(error)}`);
    } finally {
      setIsProcessing(false);
    }
  };

  // Render step content
  const renderStepContent = () => {
    const result = stepResults[currentStep];
    const error = stepErrors[currentStep];

    if (error) {
      return (
        <Alert variant="danger">
          <Alert.Heading className="step-heading-title">Step Failed</Alert.Heading>
          <p className="step-description">{error.error}</p>
          <small className="text-muted">
            Failed at: {error.timestamp?.toLocaleString()}
          </small>
        </Alert>
      );
    }

    if (!result) {
      return (
        <Alert variant="info">
          <Alert.Heading className="step-heading-title">
            {PIPELINE_STEPS[currentStep].icon} {PIPELINE_STEPS[currentStep].name}
          </Alert.Heading>
          <p className="step-description">{PIPELINE_STEPS[currentStep].description}</p>
          <hr />
          <p className="mb-0">
            <strong className="modal-section-title">Details:</strong> {PIPELINE_STEPS[currentStep].details}
          </p>
        </Alert>
      );
    }

    // Render appropriate result component based on step
    switch (currentStep) {
      case 0:
        return <ConfigUnpackResults result={result} />;
      case 1:
        return <ScalingReferenceResults result={result} />;
      case 2:
        return <WeightsMetricResults result={result} />;
      case 3:
        return <PhasesScorerAnomaliesResults result={result} />;
      case 4:
        return <EventLogFPSuppressionResults result={result} />;
      case 5:
        return <LiftLagRCAResults result={result} />;
      case 6:
        return <SensorHealthTrendsResults result={result} />;
      case 7:
        return <TieredMLResults result={result} />;
      case 8:
        return <AssembleResultResults result={result} />;
      case 9:
        return <AuditBundleResults result={result} />;
      default:
        return (
          <Alert variant="success">
            <Alert.Heading className="step-heading-title">✅ {result.stepName}</Alert.Heading>
            <p className="step-description">{result.message}</p>
            <details>
              <summary className="text-primary modal-section-title" style={{cursor: 'pointer'}}>
                <strong>View Step Data</strong>
              </summary>
              <pre className="small text-muted mt-2" style={{maxHeight: '300px', overflow: 'auto'}}>
                {JSON.stringify(result.data, null, 2)}
              </pre>
            </details>
          </Alert>
        );
    }
  };

  return (
    <Modal 
      show={show} 
      onHide={onHide} 
      size="xl" 
      className="pipeline-modal"
      backdrop="static"
      keyboard={!isProcessing}
    >
      <Modal.Header closeButton={!isProcessing}>
        <Modal.Title>
          🔧 Pipeline Analysis - {projectData?.name || 'Project'}
        </Modal.Title>
      </Modal.Header>
      
      <Modal.Body>
        {/* Action Buttons */}
        <div className="bg-light border rounded p-3 mb-3">
          <div className="d-flex justify-content-between align-items-center">
            <div>
              <Button 
                variant="info" 
                onClick={handleRunAllSteps}
                disabled={isProcessing}
                className="me-2"
              >
                {isProcessing ? <Spinner animation="border" size="sm" className="me-2" /> : '🚀 '}
                Run All Steps
              </Button>
              <Button 
                variant="primary" 
                onClick={handleRunStep}
                disabled={isProcessing}
              >
                {isProcessing ? <Spinner animation="border" size="sm" className="me-2" /> : '▶️ '}
                Run Step {currentStep + 1}
              </Button>
            </div>
            <div>
              <Button 
                variant="outline-secondary" 
                onClick={onHide}
                disabled={isProcessing}
              >
                {isProcessing ? 'Processing...' : 'Close'}
              </Button>
            </div>
          </div>
        </div>

        {/* Global Error Alert */}
        {error && (
          <Alert variant="danger" dismissible onClose={() => setError('')}>
            <Alert.Heading>Pipeline Error</Alert.Heading>
            <p>{error}</p>
          </Alert>
        )}

        {/* Progress Bar */}
        <ProgressBar 
          currentStep={currentStep}
          stepResults={stepResults}
          stepErrors={stepErrors}
        />

        <Row>
          {/* Left Column - Step Navigation */}
          <Col md={4}>
            <StepNavigation
              currentStep={currentStep}
              stepResults={stepResults}
              stepErrors={stepErrors}
              onStepClick={handleStepClick}
              executionOrder={executionOrder}
            />
          </Col>

          {/* Right Column - Step Content */}
          <Col md={8}>
            {renderStepContent()}
          </Col>
        </Row>
      </Modal.Body>
      
      <Modal.Footer>
        <div className="text-muted small">
          Pipeline Analysis Tool - {Object.keys(stepResults).length} of {PIPELINE_STEPS.length} steps completed
        </div>
      </Modal.Footer>
    </Modal>
  );
};

export default PipelineModal;
